import { useMemo } from 'react';
import { enableSupportedWarehouseExperimentationManifests } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { IntegrationManifest } from '@gonfalon/integrations/types';
import { integrationConfigurationsQuery } from '@gonfalon/rest-api/queries/integrationConfigurations';
import { getIntegrationManifestQuery } from '@gonfalon/rest-api/queries/integrationManifests';
import type { IntegrationConfiguration } from '@gonfalon/rest-api/types';
import { useQueries } from '@tanstack/react-query';

import { warehouseExperimentationConfigValuesSchema } from './utils';

export type UseWarehouseExpIntegrationsResult = {
  integrationConfigs: IntegrationConfiguration[];
  supportedManifests: IntegrationManifest[];
  isLoading: boolean;
  hasIntegrations: boolean;
  hasSupportedManifests: boolean;
};

export const useWarehouseExpIntegrations = ({
  disabled,
  filter,
}: {
  disabled?: boolean;
  filter?: { projectKey?: string; environmentKey?: string };
} = {}): UseWarehouseExpIntegrationsResult => {
  const warehouseExpManifestKeys = enableSupportedWarehouseExperimentationManifests();
  const isEnabled = !disabled;
  const manifestQueries = useQueries({
    queries: warehouseExpManifestKeys.map((manifestKey) => ({
      ...getIntegrationManifestQuery({ integrationKey: manifestKey }),
      enabled: isEnabled,
    })),
  });
  const manifests = manifestQueries.map((query) => query.data);
  const supportedManifests = useMemo(
    () =>
      manifestQueries.flatMap((query) => {
        const manifest = query.data;

        return manifest ? [manifest] : [];
      }),
    [manifestQueries],
  );
  const integrationConfigQueries = useQueries({
    queries:
      manifests
        ?.filter((manifest) => manifest?.allowIntegrationConfigurations)
        .map((manifest) => ({
          ...integrationConfigurationsQuery({ integrationKey: manifest?.key ?? '' }),
          enabled: isEnabled,
        })) ?? [],
  });
  const integrationConfigs = useMemo(() => {
    return integrationConfigQueries.reduce<IntegrationConfiguration[]>((accumulator, currentQuery) => {
      if (currentQuery.data?.items) {
        const items = currentQuery.data.items;

        return [
          ...accumulator,
          ...items.filter((ic) => {
            const projectKeyFilter = filter?.projectKey;
            const envKeyFilter = filter?.environmentKey;

            if (projectKeyFilter || envKeyFilter) {
              const { data, success } = warehouseExperimentationConfigValuesSchema.safeParse(ic?.configValues);

              if (!success) {
                return false;
              }

              if (projectKeyFilter && data.selectedEnv.projectKey !== projectKeyFilter) {
                return false;
              }

              if (envKeyFilter && data.selectedEnv.environmentKey !== envKeyFilter) {
                return false;
              }

              return true;
            }

            return true;
          }),
        ];
      }
      return accumulator;
    }, []);
  }, [integrationConfigQueries, filter?.projectKey, filter?.environmentKey]);
  const isLoading =
    manifestQueries.some((query) => query.isPending) || integrationConfigQueries.some((query) => query.isPending);

  return {
    integrationConfigs,
    supportedManifests,
    isLoading,
    hasIntegrations: integrationConfigs.length > 0,
    hasSupportedManifests: supportedManifests.length > 0,
  };
};
